import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router';
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import LoadingScreen from '../components/util/LoadingScreen';
import { PATH_DASHBOARD, ROOTS_APP } from './paths';
import { AdminTab } from '../pages/admin/Admin';
import { CampaignsTab } from '../pages/campaigns/Campaigns';
import useAuth from '../hooks/useAuth';
import { IVRTab } from '../pages/ivr/IVR';
import { OrganisationTab } from '../pages/organizations/Organization';
import { OrdersTab } from '../pages/orders/Orders';
import AccessGuard, { legacyRoleRules, rules } from 'access';
import { FeatureFlagId } from '../@types/featureFlags';

const Loadable = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => {
  const LoadableComponent = (props: any) => {
    const { isDataReflectingCurrentOrg, isInitialized } = useAuth();
    const pathname = globalThis.location.pathname;
    const isDashboard = pathname.includes('/home');

    return (
      <Suspense
        fallback={
          <div>
            {isInitialized && isDataReflectingCurrentOrg && (
              <LoadingScreen
                sx={{
                  ...(!isDashboard && {
                    top: 0,
                    left: 0,
                    width: 1,
                    zIndex: 9999,
                    position: 'fixed'
                  })
                }}
              />
            )}
          </div>
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };

  return LoadableComponent;
};

export default function Router() {
  return useRoutes([
    {
      path: ROOTS_APP.substring(1),
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to={`${ROOTS_APP}/dashboard`} replace /> },
        { path: 'dashboard', element: <DashboardOverview /> },
        {
          path: 'user',
          children: [
            { element: <Navigate to={`${ROOTS_APP}/user/profile`} replace /> },
            { path: 'profile', element: <UserAccount /> },
            { path: 'settings', element: <CustomizeSettings /> }
          ]
        },
        {
          path: 'campaigns',
          children: [
            {
              index: true,
              element: (
                <Navigate
                  to={`${ROOTS_APP}/campaigns/tabs/${CampaignsTab.ACTIVE_CAMPAIGNS}`}
                  replace
                />
              )
            },
            {
              path: 'tabs/:tab',
              children: [{ index: true, element: <Campaigns /> }]
            },
            { path: 'new', element: <CampaignNew /> },
            { path: ':id/edit', element: <CampaignEdit /> },
            {
              path: 'templates',
              children: [
                {
                  element: (
                    <Navigate
                      to={`${ROOTS_APP}/campaigns/tabs/${CampaignsTab.ACTIVE_CAMPAIGNS}`}
                      replace
                    />
                  )
                },
                { path: 'new', element: <TemplateNew /> },
                { path: ':id', element: <TemplateDetail /> }
              ]
            },
            {
              path: 'npsNotifications',
              children: [
                {
                  element: (
                    <Navigate
                      to={`${ROOTS_APP}/campaigns/tabs/${CampaignsTab.NPS_NOTIFICATIONS}`}
                      replace
                    />
                  )
                },
                { path: 'new', element: <NpsNotificationEdit isNew={true} /> },
                { path: ':id', element: <NpsNotificationEdit /> }
              ]
            },
            {
              path: ':id',
              children: [
                { index: true, element: <CampaignDetail /> },
                { path: 'send-survey', element: <CampaignSendSurvey /> },
                {
                  path: 'campaignMembers',
                  children: [
                    {
                      path: ':campaignMemberId',
                      element: <CampaignMemberDetail />,
                      children: [{ path: ':edit', element: <CampaignMemberDetail /> }]
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: 'companies',
          children: [{ index: true, element: <Companies /> }]
        },
        {
          path: 'account',
          children: [
            {
              index: true,
              element: <Navigate to={`${ROOTS_APP}/account/${OrganisationTab.GENERAL}`} replace />
            },
            { path: 'users/:userId', element: <OrganizationUserDetail /> },
            {
              path: ':tab',
              children: [{ index: true, element: <Organization /> }]
            }
          ]
        },
        {
          path: 'orders',
          children: [
            {
              index: true,
              element: (
                <AccessGuard rule={rules.order.read}>
                  <Navigate
                    to={`${PATH_DASHBOARD.orders.tabs}/${OrdersTab.SURVEY_ORDERS}`}
                    replace
                  />
                </AccessGuard>
              )
            },

            {
              path: 'tabs/:tab',
              element: (
                <AccessGuard rule={rules.order.read}>
                  <OrdersPage />
                </AccessGuard>
              )
            },
            {
              path: 'survey-orders/:id',
              element: (
                <AccessGuard rule={rules.order.read}>
                  <SurveyOrderDetail />
                </AccessGuard>
              )
            },
            {
              path: 'survey-orders/new',
              element: (
                <AccessGuard rule={rules.order.create}>
                  <NewSurveyOrder />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'product-orders',
          children: [
            {
              index: true,
              element: (
                <AccessGuard rule={rules.order.read}>
                  <Navigate to={PATH_DASHBOARD.productOrders.productOrdersTab} replace />
                </AccessGuard>
              )
            },
            {
              path: 'tabs/:tab',
              element: (
                <AccessGuard rule={rules.order.read}>
                  <ProductOrders />
                </AccessGuard>
              )
            },
            {
              path: 'product-orders/:id',
              element: (
                <AccessGuard rule={rules.order.read}>
                  <ProductOrderDetail />
                </AccessGuard>
              )
            },
            {
              path: 'product-orders/new',
              element: (
                <AccessGuard rule={rules.order.create}>
                  <NewProductOrder />
                </AccessGuard>
              )
            },
            {
              path: 'billable-hours-orders/:id',
              element: (
                <AccessGuard rule={rules.order.read}>
                  <BillableHoursOrderDetail />
                </AccessGuard>
              )
            },
            {
              path: 'billable-hours-orders/new',
              element: (
                <AccessGuard rule={rules.order.create}>
                  <NewBillableHoursOrder />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'respondents',
          children: [
            { element: <Respondents />, index: true },
            {
              path: ':id',
              element: <RespondentDetail />,
              children: [{ path: ':edit', element: <RespondentDetail /> }]
            }
          ]
        },
        {
          path: 'p-gap',
          children: [
            {
              index: true,
              element: (
                <AccessGuard rule={rules.module.pgap} displayNoAccess={true}>
                  <PGap />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'discover-integrations',
          children: [
            {
              index: true,
              element: <DiscoverIntegrations />
            }
          ]
        },
        {
          path: 'ivr',
          children: [
            {
              index: true,
              element: <Navigate to={`${ROOTS_APP}/ivr/${IVRTab.CONNECTIONS}`} replace />
            },
            {
              path: ':tab',
              children: [
                {
                  index: true,
                  element: (
                    <AccessGuard rule={rules.module.ivr} displayNoAccess={true}>
                      <IVR />
                    </AccessGuard>
                  )
                }
              ]
            }
          ]
        },
        {
          path: 'etrack1',
          children: [
            {
              index: true,
              element: (
                <AccessGuard rule={rules.module.etrack1} displayNoAccess={true}>
                  <Etrack1 />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'product-configurations',
          children: [
            {
              index: true,
              element: (
                <AccessGuard displayNoAccess={true} rule={legacyRoleRules.adminOnly}>
                  <ProductConfigurations />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'ai-feedback',
          children: [
            {
              index: true,
              element: (
                <AccessGuard
                  rule={rules.aiFeedback.read}
                  featureFlagAccess={FeatureFlagId.FeedbackAnalysis}
                  displayNoAccess={true}
                >
                  <AiFeedback />
                </AccessGuard>
              )
            }
          ]
        },
        {
          path: 'ai-feedback-usage',
          element: (
            <AccessGuard rule={rules.tokenUsage.readGlobal} displayNoAccess={true}>
              <AiFeedbackUsage />
            </AccessGuard>
          )
        },
        {
          path: 'reports',
          children: [{ index: true, element: <Reports /> }]
        },
        {
          path: 'reviews',
          children: [
            {
              index: true,
              element: (
                <AccessGuard rule={rules.module.reviews} displayNoAccess={true}>
                  <Reviews />
                </AccessGuard>
              )
            }
          ]
        },
        { path: 'employees', element: <Employees /> },
        {
          path: 'admin',
          children: [
            {
              index: true,
              element: <Navigate to={`${ROOTS_APP}/admin/${AdminTab.ORGANIZATIONS}`} replace />
            },
            {
              path: ':tab',
              children: [
                {
                  index: true,
                  element: (
                    <AccessGuard rule={rules.adminArea} displayNoAccess={true}>
                      <Admin />
                    </AccessGuard>
                  )
                }
              ]
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        {
          index: true,
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'login/*',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'authorize',
          element: (
            <AuthGuard>
              <AuthorizeApplication />
            </AuthGuard>
          )
        },
        {
          path: 'forgot-password',
          element: (
            <GuestGuard>
              <ForgotPassword />
            </GuestGuard>
          )
        },
        {
          path: 'update-password',
          element: (
            <GuestGuard>
              <ResetPassword />
            </GuestGuard>
          )
        },
        {
          path: 'sign-up',
          element: (
            <GuestGuard>
              <SignUp />
            </GuestGuard>
          )
        }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}
// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const AuthorizeApplication = Loadable(
  lazy(() => import('../pages/authentication/AuthorizeApplication'))
);
const ForgotPassword = Loadable(lazy(() => import('../pages/authentication/ForgotPassword')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const SignUp = Loadable(lazy(() => import('../pages/authentication/SignUp')));
// Dashboard
const Campaigns = Loadable(lazy(() => import('../pages/campaigns/Campaigns')));
const Companies = Loadable(lazy(() => import('../pages/companies/Companies')));
const CampaignDetail = Loadable(lazy(() => import('../pages/campaigns/CampaignDetail')));
const CampaignSendSurvey = Loadable(lazy(() => import('../pages/campaigns/CampaignSendSurvey')));
const CampaignEdit = Loadable(lazy(() => import('../pages/campaigns/CampaignEdit')));
const CampaignNew = Loadable(lazy(() => import('../pages/campaigns/CampaignNew')));
const DashboardOverview = Loadable(lazy(() => import('../pages/dashboard/DashboardOverview')));
const Employees = Loadable(lazy(() => import('../pages/employees/Employees')));
const Organization = Loadable(lazy(() => import('../pages/organizations/Organization')));
const OrganizationUserDetail = Loadable(
  lazy(() => import('../pages/organizations/OrganizationUserDetail'))
);
const UserAccount = Loadable(lazy(() => import('../pages/account/UserAccount')));
const CustomizeSettings = Loadable(lazy(() => import('../pages/customize/CustomizeSettings')));
const DiscoverIntegrations = Loadable(
  lazy(() => import('../pages/discoverIntegrations/DiscoverIntegrations'))
);
const IVR = Loadable(lazy(() => import('../pages/ivr/IVR')));
const Etrack1 = Loadable(lazy(() => import('../pages/etrack1/Etrack1')));
const ProductConfigurations = Loadable(
  lazy(() => import('../pages/productConfiguration/ProductConfigurations'))
);

const AiFeedback = Loadable(lazy(() => import('../pages/ai/AiFeedback')));
const AiFeedbackUsage = Loadable(lazy(() => import('../pages/ai/AiFeedbackUsage')));
const PGap = Loadable(lazy(() => import('../pages/pgap/PGap')));
const Respondents = Loadable(lazy(() => import('../pages/respondents/Respondents')));
const RespondentDetail = Loadable(lazy(() => import('../pages/respondents/RespondentDetail')));
const TemplateDetail = Loadable(lazy(() => import('../pages/templates/TemplateDetail')));
const TemplateNew = Loadable(lazy(() => import('../pages/templates/TemplateNew')));
const OrdersPage = Loadable(lazy(() => import('../pages/orders/OrdersPage')));
const ProductOrders = Loadable(lazy(() => import('../pages/orders/ProductOrders')));
const SurveyOrderDetail = Loadable(
  lazy(() => import('../pages/orders/components/surveyOrder/SurveyOrderDetails'))
);
const ProductOrderDetail = Loadable(
  lazy(() => import('../pages/orders/components/productOrder/ProductOrderDetails'))
);
const BillableHoursOrderDetail = Loadable(
  lazy(() => import('../pages/orders/components/billableHoursOrder/BillableHoursOrderDetails'))
);
const NewSurveyOrder = Loadable(
  lazy(() => import('../pages/orders/components/surveyOrder/NewSurveyOrderForm'))
);
const NewBillableHoursOrder = Loadable(
  lazy(() => import('../pages/orders/components/billableHoursOrder/NewBillableHoursOrderForm'))
);
const NewProductOrder = Loadable(
  lazy(() => import('../pages/orders/components/productOrder/NewProductOrderForm'))
);
const NpsNotificationEdit = Loadable(
  lazy(() => import('../pages/npsNotifications/NpsNotificationEdit'))
);
const Reports = Loadable(lazy(() => import('../pages/reports/Reports')));
const Reviews = Loadable(lazy(() => import('../pages/reviews/Reviews')));
const CampaignMemberDetail = Loadable(
  lazy(() => import('../pages/campaignMember/CampaignMemberDetail'))
);
// Admin
const Admin = Loadable(lazy(() => import('../pages/admin/Admin')));
// Main
const Page500 = Loadable(lazy(() => import('../pages/fallback/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/fallback/Page404')));
